
import { Component, Mixins } from "vue-property-decorator";
import { FixedPhraseTextDropManager, IImageDragParameter, DocumentDataDropManager } from "../../../../dragging";
import { CommonComponentsAccessibleMixin } from "../../mixins";
import FreeInputableMixin from "./FreeInputableMixin.vue";
import { instanceOfIDcoumentDataDroppableObject } from "..";

@Component
export default class TextFreeInputableMixin extends Mixins(FreeInputableMixin, CommonComponentsAccessibleMixin) {
  freeInputText: string;

  data(): any {
    return {
      inputableElement: null as HTMLInputElement,
    };
  }
  inputableElement: HTMLInputElement;

  getSelectedText(): string {
    // 元々window.getSelection()で取っていたが、IE11でtextareaの選択が取れない.(bugらしい.旧edgeでは直ってる模様.)
    // やむを得ないのでobject側に実装.
    return (this.inputableElement && (this.inputableElement.selectionStart < this.inputableElement.selectionEnd))
      ? this.inputableElement.value.slice(this.inputableElement.selectionStart, this.inputableElement.selectionEnd)
      : "";
  }

  removeSelectedText(): void {
    if (!this.inputableElement || this.inputableElement.selectionStart >= this.inputableElement.selectionEnd) { return; }
    const caretIndex = this.inputableElement.selectionStart;
    const firstHarf = this.inputableElement.value.slice(0, this.inputableElement.selectionStart);
    const secondHarf = this.inputableElement.value.slice(this.inputableElement.selectionEnd);
    this.freeInputText = firstHarf + secondHarf;
    this.$nextTick(() => {
      if (this.inputableElement) {
        this.inputableElement.focus();
        this.inputableElement.setSelectionRange(caretIndex, caretIndex);
      }
    });
  }

  getCaretIndex(): number {
    if (!this.inputableElement) {
      this.$logger.error("inputableElementが設定されていません。実装を確認してください。");
      return undefined;
    }
    return this.inputableElement.selectionStart;
  }
  
  insertTextToCaretPosition(value: string): void {
    const text = this.inputableElement ? this.inputableElement.value : "";
    const caretIndex = this.getCaretIndex();
    const index = (caretIndex !== undefined) ? caretIndex : text.length;
    const selectionEnd = this.inputableElement ? this.inputableElement.selectionEnd : 0;
    const inserted = text.slice(0, index) + value + text.slice(Math.max(index, selectionEnd));
    this.freeInputText = inserted;
    this.$nextTick(() => {
      if (this.inputableElement) {
        const insertedIndex = index + value.length;
        this.inputableElement.focus();
        this.inputableElement.setSelectionRange(insertedIndex, insertedIndex);
      }
    });
  }
  
  onDragOverFreeInput(event: DragEvent): void {
    // 優先度が高い順に並んでいるそうなので先頭を取得, getData("text")でとれる値も先頭と思われる
    const dragDataType = event.dataTransfer.types[0];
    /*
      テキストをドラッグ&ドロップしたときのデータの型がブラウザによって異なり、同じテキストでも
      ieでは["Text", "URL"]、chromeでは["text/plain", "text/uri-list", "text/html"]となったりする。
      ieとchromeでテキストを取得できればとりあえず大丈夫だと思うので、
      "Text"と"text/plain"が優先度の高い先頭であればテキストがドラッグされているとしている。
      https://developer.mozilla.org/ja/docs/DragDrop/Recommended_Drag_Types

      クライオ画像の貼り付けは情報をjsonのテキストで持っているがこれはドロップ時の処理で排除する
    */
    const isDragDataText = (dragDataType === "Text" || dragDataType === "text/plain");
    if (isDragDataText && this.isFreeInputable && this.shouldInputFreeSelfOrAll) {
      if (this.isEventOnLabel(event)) {
        event.preventDefault();
      } else {
        // input要素のデフォルトアクションでキャレット位置を指定するためpreventDefault()を呼ばない
      }
      event.stopPropagation();
      event.dataTransfer.dropEffect = "copy";
    }
  }

  async onDropFreeInput(event: DragEvent, emitFocus: () => void): Promise<void> {
    // 定型文をドロップした場合、パラメータがクリアされないためここでクリアする
    const manager = new FixedPhraseTextDropManager();
    const droppedPhrase = manager.parameter; // AI定型文はここでハンドルするのでクリアする前に退避しておく.
    manager.parameter = null;
    const isAiPhrase = (droppedPhrase && droppedPhrase.isAiPhrase);

    // 過去データをドロップした場合、パラメータがクリアされないためここでクリアする
    const documentDataDropParam = DocumentDataDropManager.parameter;
    DocumentDataDropManager.parameter = null;

    // 画像がドロップされたときは画像を貼り付けるために処理をしない
    // src\components\dragging\ImageDropManager.ts
    const text = event.dataTransfer.getData("text");
    if (text == null || text === "") { return; }
    if (this.isDragDataImageRelatedJsonText(text)) { return; }

    if (this.isEditDisabledByRuleSet) {
      // 日付オブジェクトの入力禁止が甘いため、ここでガードしている
      return;
    }

    if (this.isFreeInputable && this.shouldInputFreeSelfOrAll) {
      if (isAiPhrase) {
        event.stopPropagation();
        event.preventDefault();
        const aiCreatedText = await this.getAiCreatedText(text);
        if (aiCreatedText) {
          this.freeInputText = aiCreatedText;
        }
      } else if (this.isEventOnLabel(event)) {
        event.stopPropagation();
        event.preventDefault();
        // TODO: insertTextToCaretPositionを使うことも検討したがinputableElementを実装していないコンポーネントもあるため見送っている
        this.freeInputText = this.freeInputText + text;
      } else {
        // DnDによるテキスト貼り付けはinput要素のデフォルトアクションで行うのでpreventDefault()を呼ばない
        event.stopPropagation();
      }
      const element = (event.target as Element).querySelector<HTMLElement>("input, textarea");
      if (element) {
        element.focus();
      }
      emitFocus();
    } else if (!documentDataDropParam || documentDataDropParam.text === text) {
      // 日付オブジェクトに過去データからD&Dで日付を反映させる暫定対応
      // HIS選択状態のフリー入力もこのルートで対応するようにしている
      // ユーザ定義も同様の対応が必要そうだが、固定文字も考慮する必要あり現状では未対応
      // textを比較することで、過去データ以外からのDropで異なる日付が格納されることを防いでいる
      if (instanceOfIDcoumentDataDroppableObject(this)) {
        if (this.isFreeInputable && isAiPhrase) {
          const aiCreatedText = await this.getAiCreatedText(text);
          if (aiCreatedText) {
            this.setDroppedDocumentData(aiCreatedText, documentDataDropParam);
          }
          return;
        }
        this.setDroppedDocumentData(text, documentDataDropParam);
        // TODO: できればここでfocusを当てるようにしたい
      }
    }
  }

  /**
   * 上下寄せの対応のため、余白がlabel要素になっている
   * そこにD&Dされたときは自前でイベントの対応する必要があるのでtargetで判定している
   */
  private isEventOnLabel(event: Event): boolean {
    const target = event.target as HTMLElement;
    return (target && target.localName && target.localName.toLowerCase() === "label");
  }

  private isDragDataImageRelatedJsonText(json: string): boolean {
    try {
      const data = JSON.parse(json) as IImageDragParameter;
      return (data != null && (data.isClaioImage || data.isOcojoImage || data.isClaioSchema || data.isExternalImage || data.isRegisteredImage));
    } catch {
      return false; // jsonのパースに失敗しているのでIImageDragParameterではない
    }
  }

  private async getAiCreatedText(source: string): Promise<string> {
    const result = await this.commonComponents.dialogs.aiPhrase.show({
      template: source,
      actions: {
        overwrite: true,
        insertToLast: true,
        // TODO: ↑に準じてinsertTextToCaretPositionの利用は回避.上書きか末尾への追加に留める.
      },
    });
    return result.isConfirmed
      ? (result.action === "insertToLast")
        ? this.freeInputText + result.phrase
        : result.phrase
      : "";
  }
}
